// Api.jsx

import React, { useEffect, useMemo, useState } from 'react';

// Context
import { useNodeTypes } from 'context/NodeTypesContext';
import { usePublishShellChrome, ShellSectionActions } from 'context/ShellChromeContext';
import { useTheme } from 'context/ThemeContext';
import { useBase } from 'context/BaseContext';

// Utilities
import { cleanString } from './api/apiUtils';

// Hooks
import useApi from './api/hooks/useApi';
import useApiDownloads from './api/hooks/useApiDownloads';
import useBaseAccess from 'hooks/useBaseAccess';
import { useIsMobile } from 'hooks/useDeviceDetect';

// CSS
import './Api.css';

// Components
import ApiComposer from './api/composer/ApiComposer';
import ApiEndpoints from './api/endpoints/ApiEndpoints';
import ApiDownloadActions from './api/header/ApiDownloadActions';
import ApiHeader from './api/header/ApiHeader';
import ApiIntegrations from './api/integrations/ApiIntegrations';
import ApiKeys from './api/keys/ApiKeys';
import Tabs from 'components/tabs/Tabs';

const TAB_KEYS = 'keys';
const TAB_INTEGRATIONS = 'integrations';
const TAB_COMPOSER = 'composer';
const TAB_ENDPOINTS = 'endpoints';

/**
 * Api
 *
 * Base API explorer for the standalone page and settings modal embed.
 *
 * @param {boolean} [hideHeader=false] - Hide the standalone page header.
 * @param {function} [onHeaderActionsChange] - Receives header download actions when embedded in settings.
 */
const Api = ({
  hideHeader = false,
  onHeaderActionsChange,
}) => {
  const { theme } = useTheme();
  const { activeBase } = useBase();
  const { getAll: getAllNodeTypes, isReady: isNodeTypesReady } = useNodeTypes();
  const isMobile = useIsMobile();

  const [activeTab, setActiveTab] = useState(TAB_INTEGRATIONS);

  const baseId = useMemo(
    () => cleanString(activeBase?.key || activeBase?.id),
    [activeBase?.id, activeBase?.key],
  );

  const shellChromeTitle = useMemo(() => {
    const resolvedTitle = String(activeBase?.title || 'Space').trim();
    return resolvedTitle ? `${resolvedTitle} - API` : 'Base API';
  }, [activeBase?.title]);

  const hideHeaderIdentity = !hideHeader && !isMobile;
  const actionsInShell = hideHeaderIdentity;

  usePublishShellChrome({
    title: shellChromeTitle,
    iconKey: 'api',
    enabled: hideHeaderIdentity,
  });

  const { baseRole } = useBaseAccess(activeBase);

  const baseTabs = useMemo(() => ([
    {
      key: TAB_INTEGRATIONS,
      label: 'Integrations',
      onClick: () => setActiveTab(TAB_INTEGRATIONS),
    },
    {
      key: TAB_KEYS,
      label: 'API Keys',
      onClick: () => setActiveTab(TAB_KEYS),
    },
    {
      key: TAB_COMPOSER,
      label: 'Composer',
      onClick: () => setActiveTab(TAB_COMPOSER),
    },
    {
      key: TAB_ENDPOINTS,
      label: 'Endpoints',
      onClick: () => setActiveTab(TAB_ENDPOINTS),
    },
  ]), []);

  const {
    loading,
    error,
    schemaDoc,
    openapiGeneratedAt,
    visibleOpenapiPathRows,
    schemaTypes,
    nodeTypeCacheReady,
  } = useApi({
    baseId,
    getAllNodeTypes,
    isNodeTypesReady,
  });

  const {
    canDownloadOpenApi,
    canDownloadSchema,
    handleDownloadOpenApi,
    handleDownloadSchema,
  } = useApiDownloads({ baseId, schemaDoc });

  const headerActions = useMemo(() => (
    <ApiDownloadActions
      compact
      canDownloadOpenApi={canDownloadOpenApi}
      canDownloadSchema={canDownloadSchema}
      onDownloadOpenApi={handleDownloadOpenApi}
      onDownloadSchema={handleDownloadSchema}
    />
  ), [
    canDownloadOpenApi,
    canDownloadSchema,
    handleDownloadOpenApi,
    handleDownloadSchema,
  ]);

  useEffect(() => {
    if (!hideHeader || !onHeaderActionsChange) return undefined;

    onHeaderActionsChange(headerActions);
    return () => onHeaderActionsChange(null);
  }, [headerActions, hideHeader, onHeaderActionsChange]);

  return (
      <div className="api-container">
        {!hideHeader && (
          <ApiHeader
            hideIdentity={hideHeaderIdentity}
            hideActions={actionsInShell}
            canDownloadOpenApi={canDownloadOpenApi}
            canDownloadSchema={canDownloadSchema}
            onDownloadOpenApi={handleDownloadOpenApi}
            onDownloadSchema={handleDownloadSchema}
          />
        )}

        {loading && (
          <div className="api-message">
            Loading...
          </div>
        )}

        {!loading && error && (
          <div
            className="api-message api-message-error"
            style={{
              borderColor: theme.warning,
              color: theme.warning,
            }}
          >
            {error}
          </div>
        )}

        {!loading && !error && (
          <>
            <div className="api-tabs">
              <Tabs
                tabs={baseTabs}
                selectedKey={activeTab}
              />
            </div>

            {activeTab === TAB_KEYS && (
              <ApiKeys
                baseId={baseId}
                baseRole={baseRole}
                schemaTypes={schemaTypes}
              />
            )}

            {activeTab === TAB_INTEGRATIONS && (
              <ApiIntegrations
                baseId={baseId}
                baseRole={baseRole}
                schemaTypes={schemaTypes}
              />
            )}

            {activeTab === TAB_COMPOSER && (
              <ApiComposer
                baseId={baseId}
                nodeTypeCacheReady={nodeTypeCacheReady}
                schemaTypes={schemaTypes}
              />
            )}

            {activeTab === TAB_ENDPOINTS && (
              <ApiEndpoints
                theme={theme}
                visibleOpenapiPathRows={visibleOpenapiPathRows}
                openapiGeneratedAt={openapiGeneratedAt}
              />
            )}
          </>
        )}

        {!hideHeader && actionsInShell ? (
          <ShellSectionActions>
            <ApiDownloadActions
              canDownloadOpenApi={canDownloadOpenApi}
              canDownloadSchema={canDownloadSchema}
              onDownloadOpenApi={handleDownloadOpenApi}
              onDownloadSchema={handleDownloadSchema}
            />
          </ShellSectionActions>
        ) : null}
      </div>
  );
};

Api.displayName = 'Api';

export default Api;
